import React from 'react';
import { shallowEqual } from 'react-redux';
import { domPropsSelector, rtDebug } from '.';
import { useAppSelector } from '../../../../hooks/hooks';
import useRenderDebug from '../../../../hooks/useRenderDebug';
import useDomProps from '../../../../store/selection/useDomProps';
import DefaultCellRenderer from './renderers/DefaultCellRenderer';
import { useResponsiveTableContext } from './ResponsiveTableContext';
import { globals } from '@apia/util';

interface ICell {
  columnIndex: number;
  inTableColumnIndex: number;
  rowIndex: number;
}

const Cell = ({ columnIndex, inTableColumnIndex, rowIndex }: ICell) => {
  const { name, ...rest } = useResponsiveTableContext();

  const domProps = useDomProps(
    'td',
    { columnIndex: inTableColumnIndex, rowIndex },
    domPropsSelector(name),
  );

  const cell = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];

    return tableState?.rows[rowIndex]?.cells[columnIndex];
  }, shallowEqual);

  const column = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.columns[columnIndex];
  }, shallowEqual);

  const row = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.rows[rowIndex];
  }, shallowEqual);

  const Renderer = React.useMemo(
    () => cell?.Renderer ?? DefaultCellRenderer,
    [cell?.Renderer],
  );

  useRenderDebug(rtDebug.cell, 'Cell.tsx', {
    cell,
    ...rest,
    columnIndex,
    rowIndex,
  });

  if (!Renderer || !cell)
    return row.separator ||
      globals?.getVersion()?.env !== 'development' ? null : (
      <td sx={{ background: 'red', fontSize: '30px' }}>Hola mundo</td>
    );

  return (
    <Renderer
      {...(domProps as Omit<React.HTMLProps<HTMLTableCellElement>, 'ref'>)}
      cell={cell}
      column={column}
      row={row}
    />
  );
};

export default React.memo(Cell);
